import React, { useMemo, useState, useEffect } from 'react';
import { ImageSourceSizes } from '@ag1/ui';
import { StudioHotspotImageV2 } from 'shared/studio/StudioHotspotImage/StudioHotspotImage';
import { type BaseImage, type SanityKeyed } from 'types/studio/studio';
import { navigationClickEvent } from 'analytics/events/studio';
import { Carousel, type CarouselProps } from '../Carousel';
import { ImageCarouselThumbnails } from './ImageCarouselThumbnails';
import styles from './ImageCarousel.module.scss';

type ImageCarouselExtendedProps = {
  enableThumbnails?: boolean;
  thumbnails: SanityKeyed<BaseImage>[];
  images: SanityKeyed<BaseImage>[];
  sizes?: ImageSourceSizes;
  isPdp?: boolean;
} & Omit<CarouselProps, 'slides'>;

export const ImageCarousel = ({
  enableControls = true,
  enableLoop = false,
  enableThumbnails = true,
  images = [],
  sizes,
  initialSlide = 0,
  isPdp = false,
  selectedSlide: selectedParentSlide,
  thumbnails = []
}: ImageCarouselExtendedProps) => {
  const [selectedSlide, setSelectedSlide] = useState(initialSlide);

  const onClick = (type: any) => {
    navigationClickEvent({
      location: `addToCart image ${images[selectedSlide]?.alt}`,
      navigationType: type
    });
  };

  useEffect(() => {
    if (selectedParentSlide) setSelectedSlide(selectedParentSlide);
  }, [selectedParentSlide]);

  useEffect(() => {
    if (selectedSlide > images.length - 1) setSelectedSlide(images.length - 1);
  }, [images, initialSlide, selectedSlide]);

  const imageSlides = useMemo(
    () =>
      images.map((image, index) => {
        return (
          <StudioHotspotImageV2
            image={image}
            key={image._key}
            sizes={sizes}
            eagerLoading={index === 0} // eager load the first image
          />
        );
      }),
    [images, sizes]
  );

  return (
    <div
      className={`${styles.container} ${isPdp ? styles.pdpCarousel : ''}`}
      aria-label="Image Carousel"
      role="group">
      <Carousel
        isPdp={isPdp}
        enableControls={enableControls}
        enableLoop={enableLoop}
        slides={imageSlides}
        selectedSlide={selectedSlide}
        setSelectedSlide={setSelectedSlide}
        onClick={onClick}
      />
      {enableThumbnails && (
        <ImageCarouselThumbnails
          images={thumbnails}
          selectedSlide={selectedSlide}
          sizes={sizes}
          setSelectedSlide={setSelectedSlide}
          onClick={onClick}
        />
      )}
    </div>
  );
};
